import {
  addTimelineClips,
  setClipId,
  setHasChanges,
  setIsPublishing,
  setLoadingTimeline,
  setPublishStatus,
  setTimeline,
  setTimelineList,
} from '@sonifi/wb-happenings/store/happeningsSlice';
import {
  MEDIA_KEYS,
  MEDIA_SELECTORS_KEYS,
} from '@sonifi/wb-media/constants/WbMediaConstants';
import { getSettingByProduct } from '@sonifi/wb-products/services/products.service';
import { PRODUCTS_KEYS } from '@sonifi/wb-products/constants/wbProductsConstants';
import {
  createTimelineClip,
  createTimelineListWithOptGroups,
  fetchTimelineDurationLimit,
  getTimelineDuration,
  getTimelineListWithLabels,
} from './happeningsHelper';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import apiService from '@sonifi/wb-core/services/api.service';
import { CHANNEL_EDITOR_KEYS } from '../constants/wbHappeningsConstants';
import { getMediaItemMapped } from '@sonifi/wb-media/services/mediaHelper';
import { createTimelineJob, getLatestJob } from './jobsTimeline.service';
import { parseDate } from '@sonifi/wb-widget/components/WbFormatters/WbDate';
import { setPreviewMedia } from '@sonifi/wb-media/store/mediaSlice';
import { parseISO } from 'date-fns';
import { getIdFromUrl } from '@sonifi/wb-core/utils/parseHelper';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import {
  GA_ACTIONS,
  GA_LABELS,
  sendEventGA,
} from '@sonifi/wb-core/services/ga.service';

const createPlaylist = async (playlist) => {
  try {
    const playlistMapped = [];
    for (const item of playlist) {
      const type = item.type.endsWith('s') ? item.type : `${item.type}s`; // videos, slides, audios
      let itemMapped = { ...item, type };

      if (itemMapped.id) {
        const urlMedia = `${MEDIA_KEYS.TYPE[type.toUpperCase()].KEY}/${
          item.id
        }`;
        const res = await apiService.get(urlMedia);
        const itemEmbedded = res.data._embedded[res.data.files[0]];
        itemMapped = getMediaItemMapped({
          item,
          itemEmbedded,
          mediaKey: type,
          size: {
            width: CHANNEL_EDITOR_KEYS.THUMBNAIL_SIZE.WIDTH,
            height: CHANNEL_EDITOR_KEYS.THUMBNAIL_SIZE.HEIGHT,
          },
        });
      } else {
        itemMapped = { ...itemMapped, isZombie: true };
      }

      playlistMapped.push(itemMapped);
    }
    return playlistMapped;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

const getPlaylistAudio = async (timelineAudio) => {
  try {
    if (timelineAudio?.id) {
      const audios = [];
      for (const audioFile of timelineAudio.files) {
        const fileAudioId = getIdFromUrl({ url: audioFile });
        const urlAudio = `${ENDPOINTS.FILES}/${fileAudioId}`;
        const resAudio = await apiService.get(urlAudio);
        audios.push(resAudio.data);
      }

      const defaultAudio =
        audios.find((a) => a.mimetype === 'audio/mpeg') || audios[0];

      const audio = {
        ...timelineAudio,
        ...defaultAudio,
        type: MEDIA_KEYS.TYPE.AUDIOS.KEY,
        uri: timelineAudio.uri,
      };
      return audio;
    }
    return null;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

const buildClip = (clipData) => (dispatch, getState) => {
  const thumbnail = clipData.thumbnail_pattern
    .replace('{WIDTH}', CHANNEL_EDITOR_KEYS.THUMBNAIL_SIZE.WIDTH)
    .replace('{HEIGHT}', CHANNEL_EDITOR_KEYS.THUMBNAIL_SIZE.HEIGHT);

  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);

  const media = {
    ...clipData,
    thumbnail,
    active: {
      start_date: startDate.toISOString(),
    },
  };
  const {
    happenings: { clipId, timelineSelected },
  } = getState();
  const clip = createTimelineClip({
    currentClipId: clipId,
    data: media,
    timelineSelected,
  });
  dispatch(setClipId(clip.clipId));
  return clip;
};

export const createAndAddClips =
  ({ clips, fromClip }) =>
  (dispatch) => {
    const newClips = clips.map((cl) => dispatch(buildClip(cl)));
    dispatch(addTimelineClips({ clips: newClips, fromClip }));
  };

export const selectTimeline = (id) => async (dispatch, getState) => {
  dispatch(setLoadingTimeline(LOADING_STATUS.loading));
  try {
    const {
      groups: { selectedGroup },
      happenings: { timelineList },
    } = getState();

    const happeningsChannelSettings = dispatch(
      getSettingByProduct({
        groupId: selectedGroup.id,
        productKey: PRODUCTS_KEYS.HAPPENINGS_CHANNEL,
      }),
    );

    let timelineSelected = timelineList.find((tl) => tl.id === id);
    const timelineDurationLimit = fetchTimelineDurationLimit({
      channelSettings: happeningsChannelSettings.channel_settings,
      timeline: timelineSelected,
      timelineList,
    });

    const [latest_job, playlist, audio] = await Promise.all([
      getLatestJob(timelineSelected.uri),
      createPlaylist(timelineSelected.playlist),
      getPlaylistAudio(timelineSelected.audio),
    ]);

    timelineSelected = {
      ...timelineSelected,
      audio,
      playlist,
      latest_job,
      activeTimeSupport:
        happeningsChannelSettings.active_time_support === 'day_of_month',
    };

    timelineSelected.playlist = timelineSelected.playlist.map((item, index) => {
      const {
        happenings: { clipId },
      } = getState();
      const clip = createTimelineClip({
        currentClipId: clipId,
        data: item,
        order: index,
        timelineSelected,
      });
      dispatch(setClipId(clip.clipId));
      return clip;
    });
    const timelineDuration = getTimelineDuration(timelineSelected);
    await dispatch(
      setTimeline({
        timelineDuration,
        timelineDurationLimit,
        timelineSelected,
      }),
    );
    dispatch(setLoadingTimeline(LOADING_STATUS.success));
    return timelineSelected;
  } catch (error) {
    console.error(error);
    dispatch(setLoadingTimeline(LOADING_STATUS.error));
    return Promise.reject(error);
  }
};

export const buildVideoTimelineForPreview = (date) => (dispatch, getState) => {
  const {
    happenings: { timelineSelected },
  } = getState();

  const parsedDate = parseISO(date);
  const previewMedia = {
    audio: timelineSelected.audio?.file_uri || null,
    type: MEDIA_KEYS.TYPE.CHANNEL.KEY,
    playlist: timelineSelected.playlist.filter((clip) => {
      const startValid = parsedDate >= parseISO(clip.active.start_date);
      const endValid = clip.active.end_date
        ? parsedDate <= parseISO(clip.active.end_date)
        : true;
      return startValid && endValid;
    }),
    defaultDuration: timelineSelected.default_duration,
    date,
  };

  if (!previewMedia.playlist.length) {
    return { invalid: true };
  } else {
    dispatch(
      setPreviewMedia({
        previewMedia,
        mediaSelectorKey: MEDIA_SELECTORS_KEYS.happenings,
      }),
    );
    return { invalid: false };
  }
};

export const publishTimeline = () => async (dispatch, getState) => {
  try {
    dispatch(setIsPublishing(true));
    const {
      happenings: { timelineSelected, timelineListWithOptGroups },
      groups: { selectedGroup },
    } = getState();

    const timelineBody = {
      audio: timelineSelected.audio?.uri || null,
      default_duration: timelineSelected.default_duration,
      group: timelineSelected.group.uri,
      id: timelineSelected.id,
      number: timelineSelected.number,
      playlist: timelineSelected.playlist.map((clip) => {
        if (clip.type === 'videos') {
          return {
            uri: clip.media.uri,
            type: clip.type.endsWith('s') ? clip.type.slice(0, -1) : clip.type,
            ...(timelineSelected.activeTimeSupport && {
              active: clip.media.active,
            }),
          };
        }
        return {
          duration: clip.duration,
          uri: clip.media.uri,
          type: clip.type.endsWith('s') ? clip.type.slice(0, -1) : clip.type,
          ...(timelineSelected.activeTimeSupport && {
            active: clip.media.active,
          }),
        };
      }),
      title: timelineSelected.title,
      type: timelineSelected.type,
      uri: timelineSelected.uri,
    };
    const url = `${ENDPOINTS.CHANNELS}/${timelineSelected.id}`;
    await apiService.patch(url, timelineBody);
    await createTimelineJob(timelineSelected.uri);

    const now = parseDate(new Date().toISOString());
    const labelData = timelineListWithOptGroups[0]?.options?.find(
      (item) => item.id === timelineSelected.id,
    );
    dispatch(
      setPublishStatus(
        `${
          labelData?.label || timelineSelected.title
        } has been published: ${now}.`,
      ),
    );
    dispatch(setIsPublishing(false));
    dispatch(setHasChanges(false));
    const groupUri = selectedGroup.uri;
    const urlByGroup = `${ENDPOINTS.CHANNELS}?_order_by=title&group=${groupUri}`;
    const res = await apiService.get(urlByGroup);

    const happeningsChannelSettings = dispatch(
      getSettingByProduct({
        groupId: selectedGroup.id,
        productKey: PRODUCTS_KEYS.HAPPENINGS_CHANNEL,
      }),
    );

    const timelineList = res.data.objects.map((ch) => {
      const group = res.data._embedded[ch.group];
      const audio = res.data._embedded[ch.audio];
      const playlist = ch.playlist.map((item) => {
        const file = res.data._embedded[item.uri];
        return { ...item, ...file, group };
      });
      return { ...ch, audio, group, playlist };
    });

    const timelineListWithLabels = getTimelineListWithLabels({
      timelineList,
      happeningsChannelSettings,
    });

    await Promise.all([
      dispatch(
        setTimelineList({
          timelineList,
          timelineListWithOptGroups: createTimelineListWithOptGroups(
            timelineListWithLabels,
            selectedGroup,
          ),
        }),
      ),
    ]);
    sendEventGA({ action: GA_ACTIONS.publish, label: GA_LABELS.channels });
  } catch (error) {
    console.error(error);
    dispatch(
      setPublishStatus('Timeline failed to publish, please try again later'),
    );

    dispatch(setIsPublishing(false));
  }
};
